/**
 * Why a coo-api read failed, recorded as Sentry tags on the failed /stores location request
 * (TEAM-15511). Kept apart from the pure retry predicates in `utils/networkErrors.ts` because it
 * reads page state (`navigator.onLine`, `document.visibilityState`, `utils/pageLifecycle.ts`), and
 * importing `pageLifecycle` installs window listeners.
 */
import { isNonJsonResponseError, isTransportFailure, readStatus } from '@/utils/networkErrors';
import { isPageUnloading } from '@/utils/pageLifecycle';
import { type SameOriginProbeResult } from '@/utils/sameOriginProbe';

/**
 * Closed set of causes recorded on a failed location request's Sentry event as the
 * `fetch_failure_cause` tag (TEAM-15511). A browser `TypeError` carries no HTTP status, so a
 * cancelled request, an offline device and a real failure all read alike without this.
 *
 * - `http` / `non_json` — a readable response came back; its status goes on `response_status`.
 * - `aborted` — an intentional `AbortError`.
 * - `page_unload` — the diner left the page mid-request (the browser cancels the fetch with the
 *   same `TypeError` a dropped connection gives; see `utils/pageLifecycle.ts`).
 * - `offline` — `navigator.onLine` was false when the failure was reported, or the same-origin
 *   probe (`utils/sameOriginProbe.ts`) failed too: the diner could reach neither host.
 * - `api_unreachable` — an opaque transport failure while the storefront's own origin still
 *   answered the probe: the diner's connection works, but coo-api is unreachable from it. Mostly a
 *   network that blocks or breaks `prod-coo-api.chowly.io`; during a coo-api incident, also an edge
 *   response without CORS headers (TEAM-14939), which reaches the browser as the identical error.
 * - `network` — an opaque transport failure the probe could not split (it timed out, or there was
 *   no probe to run).
 * - `other` — anything else (e.g. a schema-parse error).
 */
export const FETCH_FAILURE_CAUSES = [
  'http',
  'non_json',
  'aborted',
  'page_unload',
  'offline',
  'api_unreachable',
  'network',
  'other',
] as const;

export type FetchFailureCause = (typeof FETCH_FAILURE_CAUSES)[number];

export interface FetchFailureClassification {
  cause: FetchFailureCause;
  /** HTTP status, only when a readable response came back (`http` / `non_json`). */
  status?: number;
}

export interface FetchFailureContext {
  /** Whether the document had started unloading when the failure was reported. */
  pageUnloading?: boolean;
  /** `navigator.onLine` when the failure was reported. */
  online?: boolean;
  /** Result of the same-origin reachability probe; `skipped` when none ran. */
  probe?: SameOriginProbeResult;
}

const isAbortError = (error: unknown): boolean =>
  !!error && typeof error === 'object' && (error as { name?: unknown }).name === 'AbortError';

/** Page state at report time. Without a window (SSR) the diner reads as online and on the page. */
const readPageState = (): Required<Pick<FetchFailureContext, 'pageUnloading' | 'online'>> => ({
  pageUnloading: isPageUnloading(),
  online: typeof window !== 'undefined' ? window.navigator.onLine : true,
});

/**
 * Classify a failed coo-api read into one {@link FETCH_FAILURE_CAUSES} value. A status-bearing
 * response wins over page state: a real 503 that lands while the diner is leaving is still a
 * real 503, not a cancellation. Page state wins over the probe: a probe that failed because the
 * diner was leaving says nothing about their connection.
 */
export const classifyFetchFailure = (
  error: unknown,
  { pageUnloading = false, online = true, probe = 'skipped' }: FetchFailureContext = {},
): FetchFailureClassification => {
  const status = readStatus(error);

  if (isNonJsonResponseError(error)) return { cause: 'non_json', status };
  if (status != null) return { cause: 'http', status };
  if (isAbortError(error)) return { cause: 'aborted' };
  if (pageUnloading) return { cause: 'page_unload' };
  if (!online) return { cause: 'offline' };
  if (isTransportFailure(error)) {
    if (probe === 'ok') return { cause: 'api_unreachable' };
    if (probe === 'failed') return { cause: 'offline' };
    return { cause: 'network' };
  }
  return { cause: 'other' };
};

/**
 * Whether a failure is worth the same-origin probe: only an opaque transport failure while the
 * diner is online and still on the page. Every other cause is already decided without it.
 */
export const shouldProbeSameOrigin = (error: unknown): boolean =>
  typeof window !== 'undefined' && classifyFetchFailure(error, readPageState()).cause === 'network';

/**
 * Sentry tags for a failed location request (TEAM-15511): the cause, the status when there is
 * one, the page state at report time and the same-origin probe result. Every value is from a
 * fixed set — never response text. Passed as `ErrorReporter.captureException`'s `tags` argument,
 * so the event name, level, message and grouping stay what the Diner-Blocking Signals card
 * already counts. Built after the probe settles, so a diner who left the page while it ran is
 * still read as `page_unload`.
 */
export const buildFetchFailureTags = (
  error: unknown,
  { probe = 'skipped' }: { probe?: SameOriginProbeResult } = {},
): Record<string, string> => {
  const pageState = readPageState();
  const { cause, status } = classifyFetchFailure(error, { ...pageState, probe });

  return {
    fetch_failure_cause: cause,
    ...(status != null ? { response_status: String(status) } : {}),
    page_visibility: typeof window !== 'undefined' ? document.visibilityState : 'unknown',
    online: String(pageState.online),
    same_origin_probe: probe,
  };
};
